import LiveEventBuffer from "../../../data/services/LiveEventBuffer"
import { useRoomContext } from "@livekit/components-react"
import debounce from "lodash.debounce"
import { ConnectionState } from "livekit-client"
import { useEffect, useMemo } from "react"

const CAMERA_TOGGLE_DEBOUNCE_MS = 2000

/**
 * Un client sorti de la room ne dit plus rien de sa caméra : elle y est coupée
 * sans que personne n'y ait touché. C'est le cas de l'onglet évincé par un autre
 * onglet du même navigateur (même jeton, donc même identité LiveKit) ; le
 * backend ne distinguant les clients que par le nom du jeton, son « éteint »
 * refermait la caméra de l'onglet resté.
 *
 * Seul `Disconnected` se tait : pendant une reprise le client est toujours dans
 * la room, et rien ne redéclare plus tard un geste laissé tomber.
 *
 * @param {import("livekit-client").Room | undefined} room
 * @param {boolean} enabled
 */
export function recordCameraState(room, enabled) {
    if (!room || room.state === ConnectionState.Disconnected) return

    LiveEventBuffer.getInstance().record("cam_toggle", { enabled })
}

/**
 * Le relevé que `ControlBarM` appelle (`onOffCam`) à chaque changement de la
 * caméra locale : seul l'état tenu 2 s est écrit.
 *
 * @returns {((enabled: boolean) => void) & { cancel: () => void }}
 */
export function useCameraStateRecorder() {
    const room = useRoomContext()
    const recorder = useMemo(
        () =>
            debounce(
                (enabled) => recordCameraState(room, enabled),
                CAMERA_TOGGLE_DEBOUNCE_MS
            ),
        [room]
    )

    useEffect(() => () => recorder.cancel(), [recorder])

    return recorder
}
